/* global google */

import {
  createBaseConnectorClass,
  CONNECTOR_ERRORS,
} from '@descope-ui/common/base-classes';
import { initGoogleMapsLoader } from './googleScriptInit';

export class GoogleMapsConnector extends createBaseConnectorClass() {
  #autocompleteService = null;

  #initializationPromise = null;

  constructor(getAttribute) {
    super(getAttribute);
    // Start initialization but don't block constructor
    this.#initializationPromise = this.#initializeAutocompleteService();
  }

  // eslint-disable-next-line class-methods-use-this
  getRequiredParams() {
    return ['public-api-key'];
  }

  // eslint-disable-next-line class-methods-use-this
  getOptionalParams() {
    return [
      {
        key: 'includedPrimaryTypes',
        attribute: 'address-types',
      },
      {
        key: 'language',
        attribute: 'address-language',
      },
      {
        key: 'region',
        attribute: 'address-region',
      },
    ];
  }

  async #initializeAutocompleteService() {
    const apiKey = this.getAttribute('public-api-key');
    if (this.#autocompleteService || !apiKey) {
      return;
    }

    try {
      initGoogleMapsLoader(apiKey);

      const { AutocompleteSuggestion } =
        await google.maps.importLibrary('places');
      this.#autocompleteService = AutocompleteSuggestion;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to initialize Google Maps Autocomplete service:',
        error,
      );
      throw error;
    }
  }

  async _fetchResults(query) {
    if (!query?.trim()) {
      return { results: [] };
    }

    try {
      await this.#initializationPromise;

      const request = {
        input: query,
      };

      // Add optional parameters
      this.getOptionalParams().forEach(({ attribute, key, defaultValue }) => {
        const value = this.getAttribute(attribute) || defaultValue;
        if (value) {
          if (key === 'includedPrimaryTypes') {
            request[key] = value
              .split(',')
              .map((type) => type.trim())
              .filter(Boolean);
          } else {
            request[key] = value;
          }
        }
      });

      const { suggestions } =
        await this.#autocompleteService.fetchAutocompleteSuggestions(request);
      return this.#parseResponse(suggestions);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Google Maps Places Autocomplete failed:', error);
      return { results: [], error: CONNECTOR_ERRORS.FETCH_RESULTS_ERROR };
    }
  }

  // eslint-disable-next-line class-methods-use-this
  #parseResponse(suggestions) {
    if (!suggestions?.length) {
      return { results: [] };
    }

    return {
      results: suggestions.map((suggestion) => {
        const prediction = suggestion.placePrediction;
        return {
          id: prediction.placeId,
          label: prediction.text.text,
          value: prediction.text.text,
        };
      }),
    };
  }
}
